import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Container from "../../../Layouts/Container";
import {
  getReportDateRange,
  formatReportDate,
} from "../../../Wrapper/ReportDateUtils";
import csvIcon from "../../../assets/img/csvicon.svg";
import DownloadIcon from "../../../assets/icon/Download.svg";
import pdfIcon from "../../../assets/img/pdficon.svg";
import xlsIcon from "../../../assets/img/xlsicon.svg";
import { useFetch } from "../../../hooks/useFetch";
import useAbacAccess from "../../../hooks/useAbacAccess";
import { useDownloadData } from "../../../hooks/usePaginationNDownload";
import { hasRoles, showToast } from "../../../lib/utility";
import { middleware } from "../../../redux/store";
import { authSelector } from "../../Auth/AuthSlice";
import CloseButton from "../../Common/CloseButton";
import CommonHeader from "../../Common/CommonHeader";
import CustomSelect from "../../Common/CustomSelect";
import Modal from "../../Common/Modal";
import TableWithHeader from "../../Common/TableWithHeader";
import SubmissionTable from "../DataTable/SubmissionTable";
import SubmissionFilter from "../ReportFilterModal/SubmissionFilter";
import LoaderComponent from "../../Common/LoaderComponent";
import useOpenFilterModalWithLoader from "../useOpenFilterModalWithLoader";
import useReportMountReset from "../useReportMountReset";
import {
  clearStateSubmissionReport,
  getSavedFilter,
  reportSelector,
  submissionUserData,
} from "../ReportSlice";

const getDefaultFilterData = (bodyForDate) => ({
  enddate: formatReportDate(bodyForDate.endDate),
  startdate: formatReportDate(bodyForDate.startDate),
  reportType: null,
  country: "%",
  operator: "%",
  toGmt: "",
});

const SubmissionReport = ({ modal, toggleModal, isSeller = false }) => {
  const { t } = useTranslation("common");
  const reportName = t("report.submissionReport.title", {
    defaultValue: "Submission Report",
  });
  let bodyForDate = getReportDateRange(10);
  const { canAccessNavbar } = useAbacAccess();
  const canViewReport = canAccessNavbar("submission_report");

  const { showFilterModal, setShowFilterModal, showInitialLoader } =
    useOpenFilterModalWithLoader(true, canViewReport);
  const [searchVal, setSearchVal] = useState("");
  const [savedFilterList, setSavedFilterList] = useState([]);
  const [filterData, setFilterData] = useState(() =>
    getDefaultFilterData(bodyForDate)
  );

  const downloadTypes = [
    { value: "csv", label: "CSV", image: csvIcon },
    { value: "pdf", label: "PDF", image: pdfIcon },
    { value: "xlsx", label: "XLSX", image: xlsIcon },
  ];
  
  const dispatch = useDispatch();
  const { submissionUser, errorMessageSubmissionUser, isFetching } =
    useSelector(reportSelector);
  const { user } = useSelector(authSelector);

  const submissionData = useMemo(
    () => submissionUser?.data || submissionUser || [],
    [submissionUser]
  );

  const {
    fileType,
    pagination,
    setFileType,
    downloadContent,
    handlePageChange,
    handlePerPageChange,
  } = useDownloadData(submissionData, {
    reportName,
    paginationDefaultSize: 10,
    mappable: (data) => {
      if (!data || !Array.isArray(data)) return [];
      
      return data.map((row) => ({
        Date: row.startDate ?? "-",
        Count: row.totalSmsSend ?? 0,
        ...(filterData.country && filterData.country !== "%"
          ? { Country: row.country }
          : {}),
        ...(filterData.status && filterData.status !== "%"
          ? { Status: row.messageStatus }
          : {}),
      }));
    },
  });

  useReportMountReset(clearStateSubmissionReport, () => {
    setSearchVal("");
    setFilterData(getDefaultFilterData(bodyForDate));
  });

  const handleResetFilter = useCallback(
    (resetFilterData) => {
      dispatch(clearStateSubmissionReport());
      setSearchVal("");
      setFilterData(resetFilterData ?? getDefaultFilterData(bodyForDate));
    },
    [dispatch, bodyForDate]
  );

  const handleApplyFilter = useCallback((obj, searchValue) => {
    
    let filterObj = {
      messageStatus: obj.status !== "%" ? obj.status : undefined,
      senderId: obj.senderId !== "" ? obj.senderId : undefined,
      country: obj.country !== "%" ? obj.country : undefined,
      reportType: obj.time,
    };
    
    if (obj.clientId) {
      filterObj.clientId = obj.clientId;
    }

    if (obj.toGmt) {
      filterObj.toGmt = obj.toGmt;
    }
    
    if (obj.time === null || obj.time === undefined) {
      let startDate = obj.startdate || obj.startDate;
      let endDate = obj.enddate || obj.endDate;
      
      
      if (startDate && endDate) {
        const isFormatted = (date) => {
          return typeof date === 'string' && 
                 date.includes(' ') && 
                 date.includes(':') && 
                 date.includes('.');
        };
        
        if (isFormatted(startDate)) {
          filterObj.startdate = startDate;
        } else {
          filterObj.startdate = formatReportDate(startDate, false);
        }
        
        if (isFormatted(endDate)) {
          filterObj.enddate = endDate;
        } else {
          filterObj.enddate = formatReportDate(endDate, true);
        }
      } else {
        let bodyForDate = getReportDateRange(10);
        filterObj.startdate = formatReportDate(bodyForDate.startDate, false);
        filterObj.enddate = formatReportDate(bodyForDate.endDate, true);
      }
    } else if (obj.time === "Monthly") {
      if (obj.month) {
        filterObj.startdate = obj.month + "-01 00:00:00.000";
        filterObj.enddate = "2020-01-01 00:00:00.000";
      }
    } else if (obj.time === "Daily") {
      if (obj.date) {
        filterObj.startdate = obj.date + " 00:00:00.000";
        filterObj.enddate = "2020-01-01 00:00:00.000";
      }
    }

    setFilterData(filterObj);
    getSubmissionDataOnce(filterObj, searchValue);
  }, [filterData]);

  function getSubmissionDataOnce(obj) {
    dispatch(
      submissionUserData({
        ...(hasRoles("admin,tenant,superadmin,system,seller,manager", user)
          ? { clientId: "%" }
          : {}),
        ...filterData,
        ...obj,
      })
    ).then((action) =>
      middleware(action, reportName)
    );
  }

  const handleFileTypeChange = useCallback((e) => {
    setFileType(e);
  }, [setFileType]);

  const handleSavedFilterClick = useCallback((values) => {
    
    values.searchValue && setSearchVal(values.searchValue);
    
    const processedValues = {
      status: values.status || "%",
      senderId: values.senderId || "",
      country: values.country || "%",
      time: values.time !== undefined ? values.time : null,
      date: values.date || "",
      month: values.month || "",
      clientId: values.clientId || "%",
      searchValue: values.searchValue || "",
    };
    
    if (values.startdate) {
      processedValues.startdate = values.startdate;
    }
    if (values.enddate) {
      processedValues.enddate = values.enddate;
    }
    
    handleApplyFilter(processedValues, values.searchValue);
  }, [handleApplyFilter]);

  const getSavedFiltersList = useCallback(() => {
    dispatch(getSavedFilter("SUBMISSION")).then((action) =>
      middleware(action, "Saved Filter", () => {
        setSavedFilterList(action.payload.data);
      })
    );
  }, [dispatch]);

  useFetch(() => {
    getSavedFiltersList();
  });

  const closeButtonElement = useMemo(
    () => !isSeller && <CloseButton onClick={toggleModal} theme="dark" />,
    [isSeller, toggleModal]
  );

  const searchConfig = useMemo(
    () => ({
      searchVal,
      setSearchVal,
      saveFilterKey: "SUBMISSION",
      filterData,
      refreshFilter: getSavedFiltersList,
    }),
    [searchVal, filterData, getSavedFiltersList]
  );

  return isSeller ? (
    <Container>
      {showInitialLoader && <LoaderComponent />}
      {isSeller && (
        <CommonHeader
          name={t("report.submissionReport.title", { defaultValue: "Submission Report" })}
          fileType={fileType}
          handleFileTypeChange={handleFileTypeChange}
          downloadContent={downloadContent}
          downloadAllowed={true}
        />
      )}
      <TableWithHeader
        name={t("report.submissionReport.tableName", { defaultValue: "Submission" })}
        search={searchConfig}
        serverSearch={false}
        setShowFilterModal={setShowFilterModal}
        showSavedFilter={savedFilterList}
        handleSavedFilterClick={handleSavedFilterClick}
        customChildren={closeButtonElement}
        customChildrenPlacement={"last"}
      >
        <div
          className="commonTableHeader bg-white"
          style={{
            border: "1px solid #dedede",
            borderTop: "none",
            borderRadius: "0 0 10px 10px",
          }}
        >
          <SubmissionTable
            submissionTableData={submissionData}
            isFetching={isFetching}
            country={filterData.country}
            status={filterData.messageStatus}
            searchVal={searchVal}
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePerPageChange={handlePerPageChange}
          />
        </div>
      </TableWithHeader>
      {showFilterModal && (
        <SubmissionFilter
          modal={showFilterModal}
          closeModal={() => {
            setShowFilterModal(false);
            getSavedFiltersList();
          }}
          onResetFilter={handleResetFilter}
          onApplyFilter={handleApplyFilter}
          canSelectUsers
          initialFilterData={filterData}
        />
      )}
    </Container>
  ) : (
    <Modal justify="center" width={950} show={modal} close={toggleModal}>
      {showInitialLoader && <LoaderComponent />}
      <TableWithHeader
        name={t("report.submissionReport.tableName", { defaultValue: "Submission" })}
        search={searchConfig}
        serverSearch={false}
        setShowFilterModal={setShowFilterModal}
        showSavedFilter={savedFilterList}
        handleSavedFilterClick={handleSavedFilterClick}
        customChildren={closeButtonElement}
        customChildrenPlacement={"last"}
      >
        <div
          className="commonTableHeader bg-white"
          style={{
            border: "1px solid #dedede",
            borderTop: "none",
            borderRadius: "0 0 10px 10px",
          }}
        >
          {!isSeller && (
            <div className="d-flex justify-content-end m-3">
              <div className="dropdown download-dropdown">
                <div className="d-flex dwn-btn-border align-items-center bg-white">
                  <div
                    className="custom-select-wrapper"
                    style={{
                      position: "relative",
                      zIndex: 15,
                      borderRight: "1px solid #b5b5b5",
                      minWidth: 55,
                      flexShrink: 0,
                    }}
                  >
                    <CustomSelect
                      selectData={{
                        fileType,
                        handleFileTypeChange,
                        downloadTypes,
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn dwn-btn"
                    onClick={downloadContent}
                  >
                    <span>{t("commonUi.download", { defaultValue: "Download" })}</span>
                    <img src={DownloadIcon} alt="" loading="lazy" className="ms-2" />
                  </button>
                </div>
              </div>
            </div>
          )}
          <SubmissionTable
            submissionTableData={submissionData}
            isFetching={isFetching}
            country={filterData.country}
            status={filterData.messageStatus}
            searchVal={searchVal}
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePerPageChange={handlePerPageChange}
          />
        </div>
      </TableWithHeader>
      {showFilterModal && (
        <SubmissionFilter
          modal={showFilterModal}
          closeModal={() => {
            setShowFilterModal(false);
            getSavedFiltersList();
          }}
          onResetFilter={handleResetFilter}
          onApplyFilter={handleApplyFilter}
          canSelectUsers
          initialFilterData={filterData}
        />
      )}
    </Modal>
  );
};

export default SubmissionReport;